import {
  AccessTime,
  ContentCopy,
  Download,
  InfoOutlined,
  OpenInNew,
  PhoneIphone,
  Pool,
  Print,
  Security,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';

import CustomerIntakePublicShell from '../components/CustomerIntake/CustomerIntakePublicShell';
import { useTranslation } from '../i18n';
import {
  CustomerIntakePublicForm,
  getCurrentCustomerIntakePublicForm,
  getCustomerIntakePublicForm,
  getCustomerIntakeErrorMessage,
} from '../utils/customerIntakeApi';

const CUSTOMER_SURVEY_FORM_PATH = '/customer-survey/form';

const CustomerIntakeQr = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>(
    'idle',
  );
  const [activeForm, setActiveForm] = useState<CustomerIntakePublicForm | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // A ?token=… selects a specific form (multi-form); without it we resolve the
  // default/current form.
  const publicToken = new URLSearchParams(window.location.search).get('token');

  const loadPublicForm = async () => {
    setLoading(true);
    try {
      const form = publicToken
        ? await getCustomerIntakePublicForm(publicToken)
        : await getCurrentCustomerIntakePublicForm();
      setActiveForm(form);
      setLoadError('');
    } catch (error) {
      setLoadError(
        getCustomerIntakeErrorMessage(
          error,
          t('publicIntake.qr.loadError'),
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  // Reload when the ?token= changes; loadPublicForm reads it from closure.
  useEffect(() => {
    void loadPublicForm();
  }, [publicToken]);

  // Point the link/QR at the specific form when a token is present.
  const formUrl = (() => {
    const url = new URL(CUSTOMER_SURVEY_FORM_PATH, window.location.origin);
    const token = activeForm?.publicToken || publicToken;
    if (token) url.searchParams.set('token', token);
    return url.toString();
  })();

  const handleCopyLink = async () => {
    try {
      if (!formUrl) return;
      await navigator.clipboard.writeText(formUrl);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  };

  const handleDownloadQr = () => {
    const canvas = document.getElementById(
      'customer-survey-qr-code',
    ) as HTMLCanvasElement | null;

    if (!canvas || !formUrl) return;

    const downloadLink = document.createElement('a');
    downloadLink.download = 'aquatics-hub-customer-survey-qr.png';
    downloadLink.href = canvas.toDataURL('image/png');
    downloadLink.click();
  };

  return (
    <CustomerIntakePublicShell className="customer-intake-qr-page">
      <Container maxWidth="lg" className="customer-intake-page-container">
        <Box className="customer-intake-hero">
          <Typography className="customer-intake-eyebrow">
            {t('publicIntake.qr.eyebrow')}
          </Typography>
          <Typography
            component="h1"
            variant="h3"
            className="customer-intake-title"
            sx={{ mt: 1 }}
          >
            {t('publicIntake.qr.title')}
          </Typography>
        </Box>

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <Card className="customer-intake-card customer-intake-qr-card">
              <Stack spacing={2.5} alignItems="center">
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Pool sx={{ color: 'primary.main' }} />
                  <Typography
                    variant="h6"
                    color="secondary.main"
                    fontWeight={700}
                  >
                    {activeForm?.title || 'Customer information registration'}
                  </Typography>
                </Stack>

                <Box className="customer-intake-qr-frame">
                  <QRCodeCanvas
                    id="customer-survey-qr-code"
                    value={formUrl || 'about:blank'}
                    size={320}
                    marginSize={4}
                    bgColor="#ffffff"
                    fgColor="#102138"
                    level="H"
                  />
                </Box>

                <Typography
                  variant="body2"
                  align="center"
                  className="customer-intake-qr-note"
                  color="text.secondary"
                >
                  Please keep this page open so guests can scan the code easily.
                </Typography>

                <Box className="customer-intake-qr-actions">
                  <Button
                    variant="contained"
                    startIcon={<OpenInNew />}
                    onClick={() => navigate(CUSTOMER_SURVEY_FORM_PATH)}
                  >
                    Open form
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={handleCopyLink}
                  >
                    Copy link
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleDownloadQr}
                  >
                    Download QR
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Print />}
                    onClick={() => window.print()}
                  >
                    Print QR
                  </Button>
                </Box>

                {loading && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Loading the active form...
                    </Typography>
                  </Stack>
                )}
                {loadError && (
                  <Alert
                    severity="error"
                    sx={{ width: '100%' }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => void loadPublicForm()}
                      >
                        Try again
                      </Button>
                    }
                  >
                    {loadError}
                  </Alert>
                )}
                {copyState === 'copied' && (
                  <Alert severity="success" sx={{ width: '100%' }}>
                    Form link copied to clipboard.
                  </Alert>
                )}
                {copyState === 'error' && (
                  <Alert severity="warning" sx={{ width: '100%' }}>
                    The link could not be copied automatically. Please copy the
                    link below.
                  </Alert>
                )}
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card className="customer-intake-card customer-intake-info-card">
              <Stack spacing={1.5}>
                <Typography
                  variant="h6"
                  color="secondary.main"
                  fontWeight={700}
                >
                  How it works
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Guests can complete the form directly from their mobile phone.
                </Typography>
              </Stack>

              <Box component="ul" className="customer-intake-info-list">
                <Box component="li">
                  <PhoneIphone aria-hidden="true" />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Scan with your phone
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Point your camera at the QR Code to open the form.
                    </Typography>
                  </Box>
                </Box>
                <Box component="li">
                  <AccessTime aria-hidden="true" />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Takes about {activeForm?.estimatedMinutes || 0} minutes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      The form is short and designed for mobile completion.
                    </Typography>
                  </Box>
                </Box>
                <Box component="li">
                  <Security aria-hidden="true" />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Provide only what is needed
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Required fields are marked clearly before submission.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 3 }}>
                If scanning is not available, open the short link shown below
                the QR Code.
              </Alert>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </CustomerIntakePublicShell>
  );
};

export default CustomerIntakeQr;
